import { useCallback, useEffect, useRef } from 'preact/hooks';

import { entitlementPollDelays } from '../core/entitlement';

export type CheckoutPopupOutcome = 'paid' | 'cancelled' | 'abandoned';

interface UseCheckoutPopupOptions {
  /** Refetch the current user and answer whether the expected entitlement is now present. */
  checkEntitlement: () => Promise<boolean>;
  onConfirmed: () => void;
  /** Budget exhausted. Not an error — the card was charged (ADR 0003). */
  onUnconfirmed: () => void;
  onAbandoned: () => void;
}

const POPUP_TARGET = 'zl-speedy-checkout';
const PAID_MESSAGE = 'zl-checkout-paid';
const CANCELLED_MESSAGE = 'zl-checkout-cancelled';

/**
 * Drives the Stripe checkout tab and works out, from this tab, whether the purchase actually
 * landed.
 *
 * The whole point is that neither the popup closing nor Stripe's redirect proves entitlement —
 * both race the `checkout.session.completed` webhook that writes the purchase row. So the popup's
 * message is only a signal to go and *look*, and looking means asking the API until the gate would
 * open (ADR 0003).
 */
const useCheckoutPopup = ({ checkEntitlement, onConfirmed, onUnconfirmed, onAbandoned }: UseCheckoutPopupOptions) => {
  const popupRef = useRef<Window | null>(null);
  const watcherRef = useRef<ReturnType<typeof setInterval> | undefined>(undefined);
  const messageHandlerRef = useRef<((event: MessageEvent) => void) | null>(null);
  const pollingRef = useRef(false);
  const cancelledRef = useRef(false);

  /** Tear down the watcher and the listener for whichever `open` is currently running. */
  const cleanupRef = useRef<(() => void) | null>(null);

  /**
   * Stop everything. Called on unmount and once an outcome is reached.
   *
   * "Everything" has to include the watcher and the message listener. Flagging the polling loop and
   * leaving those two behind meant a flow dismissed mid-checkout kept a 500 ms interval alive for
   * the life of the page, and — because that interval tested neither flag — still went looking for
   * an entitlement and dispatched the outcome into a machine that no longer existed.
   */
  const stop = useCallback(() => {
    pollingRef.current = false;
    cancelledRef.current = true;
    cleanupRef.current?.();
  }, []);

  useEffect(() => stop, [stop]);

  /**
   * Ask until the entitlement appears or the budget runs out.
   *
   * Waiting is gated on visibility: while the visitor is over in the Stripe tab this one is hidden,
   * and a request fired then could not possibly succeed. Becoming visible again short-circuits the
   * current wait rather than letting it run down.
   */
  const pollForEntitlement = useCallback(async () => {
    if (pollingRef.current) {
      return;
    }
    pollingRef.current = true;

    const waitVisible = (ms: number) =>
      new Promise<void>((resolve) => {
        let timer: ReturnType<typeof setTimeout>;
        const onVisible = () => {
          if (document.visibilityState !== 'visible') {
            return;
          }
          clearTimeout(timer);
          document.removeEventListener('visibilitychange', onVisible);
          window.removeEventListener('focus', onVisible);
          resolve();
        };
        const onTimeout = () => {
          document.removeEventListener('visibilitychange', onVisible);
          window.removeEventListener('focus', onVisible);
          resolve();
        };
        timer = setTimeout(onTimeout, ms);
        document.addEventListener('visibilitychange', onVisible);
        window.addEventListener('focus', onVisible);
      });

    // The webhook usually beats the visitor back, so try once before waiting at all.
    if (await checkEntitlement()) {
      pollingRef.current = false;
      onConfirmed();
      return;
    }

    // eslint-disable-next-line no-restricted-syntax
    for (const delay of entitlementPollDelays()) {
      if (cancelledRef.current) {
        pollingRef.current = false;
        return;
      }
      // eslint-disable-next-line no-await-in-loop
      await waitVisible(delay);
      if (document.visibilityState !== 'visible') {
        // eslint-disable-next-line no-continue
        continue;
      }
      // eslint-disable-next-line no-await-in-loop
      if (await checkEntitlement()) {
        pollingRef.current = false;
        onConfirmed();
        return;
      }
    }

    pollingRef.current = false;
    onUnconfirmed();
  }, [checkEntitlement, onConfirmed, onUnconfirmed]);

  /**
   * Claim a tab now, while the click that triggered this is still being handled.
   *
   * Browsers generally only allow `window.open` during a user gesture. Creating the checkout
   * session is an async round trip, so opening the tab afterwards is liable to be treated as an
   * unsolicited popup — which would silently demote purchases to the same-tab fallback. Reserving
   * a blank tab up front and loading the URL into it later keeps the new tab the normal path.
   *
   * Note this is not observable under Playwright, which is more permissive than a real browser
   * about popups, so a passing e2e run does not prove real users are unaffected.
   */
  const reserve = useCallback(() => {
    if (popupRef.current && !popupRef.current.closed) {
      return;
    }
    popupRef.current = window.open('', POPUP_TARGET);
  }, []);

  /** Close a tab reserved for a checkout that never happened, so no blank tab is left behind. */
  const release = useCallback(() => {
    if (popupRef.current && !popupRef.current.closed) {
      popupRef.current.close();
    }
    popupRef.current = null;
  }, []);

  /**
   * Open checkout in a new tab and start watching.
   *
   * A manually closed tab emits nothing, so the closed-ness of the popup is watched too — and on a
   * close with no message we still check once, because the visitor may have paid and then closed
   * before the redirect landed.
   */
  const open = useCallback(
    (checkoutUrl: string) => {
      cancelledRef.current = false;
      // Prefer the tab reserved during the click; fall back to opening one now.
      const reserved = popupRef.current && !popupRef.current.closed ? popupRef.current : null;
      if (reserved) {
        reserved.location.href = checkoutUrl;
      }
      const popup = reserved ?? window.open(checkoutUrl, POPUP_TARGET);
      popupRef.current = popup;

      if (!popup) {
        // Popup blocked. The caller falls back to a same-tab redirect carrying the flow state.
        return false;
      }

      const cleanup = () => {
        if (watcherRef.current) {
          clearInterval(watcherRef.current);
          watcherRef.current = undefined;
        }
        if (messageHandlerRef.current) {
          window.removeEventListener('message', messageHandlerRef.current);
          messageHandlerRef.current = null;
        }
        cleanupRef.current = null;
      };
      cleanupRef.current = cleanup;

      const onMessage = (event: MessageEvent) => {
        if (event.origin !== window.location.origin) {
          return;
        }
        if (event.data?.type === PAID_MESSAGE) {
          cleanup();
          pollForEntitlement();
        }
        if (event.data?.type === CANCELLED_MESSAGE) {
          cleanup();
          onAbandoned();
        }
      };

      messageHandlerRef.current = onMessage;
      watcherRef.current = setInterval(async () => {
        // Belt and braces alongside `stop` clearing this interval: an outcome already reached, or a
        // flow already torn down, has no use for another one.
        if (cancelledRef.current) {
          cleanup();
          return;
        }
        if (!popupRef.current || !popupRef.current.closed) {
          return;
        }
        cleanup();
        // Closed without telling us anything. They may still have paid.
        if (await checkEntitlement()) {
          onConfirmed();
        } else {
          onAbandoned();
        }
      }, 500);

      window.addEventListener('message', onMessage);
      return true;
    },
    [checkEntitlement, onAbandoned, onConfirmed, pollForEntitlement],
  );

  return { open, reserve, release, retry: pollForEntitlement, stop };
};

export default useCheckoutPopup;
